import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Trophy, Medal, Crown, Flame, Dumbbell, TrendingUp, Zap, Star, ArrowUp, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Paywall from "@/components/Paywall";

export default function LeaderboardPage() {
  const { authHeaders, user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, rankRes] = await Promise.all([
        axios.get(`${API}/leaderboard?period=${period}`, { headers: authHeaders() }),
        axios.get(`${API}/leaderboard/my-rank?period=${period}`, { headers: authHeaders() })
      ]);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setMyRank(rankRes.data);
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  if (loading) {
    return (
      <Paywall>
        <div className="min-h-screen bg-[#09090b]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[1,2,3].map(i => (
                <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl h-48 animate-pulse" />
              ))}
            </div>
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl h-20 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </Paywall>
    );
  }

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="leaderboard-page">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Trophy className="h-7 w-7 text-yellow-500" />
              </div>
              <div>
                <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-white">
                  Leaderboard
                </h1>
                <p className="text-sm text-zinc-500">Konkurrér med andre atleter</p>
              </div>
            </div>
            
            {/* Period Selector */}
            <div className="flex gap-2 bg-[#18181b] border border-white/5 rounded-xl p-1.5">
              <Button
                onClick={() => setPeriod("week")}
                className={`rounded-lg px-5 h-10 ${
                  period === "week" 
                    ? "bg-[#DFFF00] text-black font-bold" 
                    : "bg-transparent text-zinc-400 hover:text-white"
                }`}
                data-testid="period-week"
              >
                Denne uge
              </Button>
              <Button
                onClick={() => setPeriod("month")}
                className={`rounded-lg px-5 h-10 ${
                  period === "month" 
                    ? "bg-[#DFFF00] text-black font-bold" 
                    : "bg-transparent text-zinc-400 hover:text-white"
                }`}
                data-testid="period-month"
              >
                Denne måned
              </Button>
            </div>
          </div>

          {/* My Rank Card */}
          {myRank && myRank.rank && (
            <div className="bg-gradient-to-r from-[#DFFF00]/10 via-[#18181b] to-[#18181b] border border-[#DFFF00]/20 rounded-xl p-5 mb-8 relative overflow-hidden" data-testid="my-rank-card">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#DFFF00]/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-xl bg-[#DFFF00]/20 flex items-center justify-center">
                      <span style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-[#DFFF00]">#{myRank.rank}</span>
                    </div>
                    {myRank.rank <= 10 && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#DFFF00] flex items-center justify-center">
                        <Star className="h-3.5 w-3.5 text-black" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Din placering</p>
                    <p className="text-2xl font-bold text-white">
                      Top <span className="text-[#DFFF00]">{myRank.percentile}%</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-center hidden sm:block">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-white">{myRank.workout_count}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Træninger</p>
                  </div>
                  <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                    <ArrowUp className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-bold text-emerald-400">Aktiv</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Podium - Top 3 */}
          {top3.length >= 3 && (
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
              {/* 2nd Place */}
              <div className="order-1" data-testid="podium-2">
                <div className="bg-gradient-to-br from-zinc-400/20 to-[#18181b] border border-zinc-400/30 rounded-xl p-4 sm:p-6 text-center h-full flex flex-col justify-end">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 rounded-xl bg-zinc-400/20 flex items-center justify-center">
                    <Medal className="h-6 w-6 sm:h-8 sm:w-8 text-zinc-400" />
                  </div>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-2 rounded-full bg-zinc-700 flex items-center justify-center text-xl sm:text-2xl font-bold text-white border-2 border-zinc-400/50">
                    {top3[1].avatar_initial}
                  </div>
                  <p className="font-bold text-white text-sm sm:text-base truncate">{top3[1].name}</p>
                  <p className="text-xs text-zinc-500 mb-2">{top3[1].level?.level_name}</p>
                  <div className="flex items-center justify-center gap-2">
                    <Dumbbell className="h-3.5 w-3.5 text-zinc-400" />
                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-zinc-300">{top3[1].workout_count}</span>
                  </div>
                </div>
              </div>
              
              {/* 1st Place */}
              <div className="order-2" data-testid="podium-1">
                <div className="bg-gradient-to-br from-yellow-500/20 to-[#18181b] border border-yellow-500/30 rounded-xl p-4 sm:p-6 text-center relative -mt-4 shadow-[0_0_30px_-5px_rgba(234,179,8,0.2)]">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="bg-yellow-500 rounded-full p-2">
                      <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-black" />
                    </div>
                  </div>
                  <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 mt-4 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                    <Trophy className="h-7 w-7 sm:h-10 sm:w-10 text-yellow-500" />
                  </div>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-2 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-2xl sm:text-3xl font-bold text-black border-4 border-yellow-300/50">
                    {top3[0].avatar_initial}
                  </div>
                  <p className="font-bold text-white text-base sm:text-lg truncate">{top3[0].name}</p>
                  <p className="text-xs text-zinc-500 mb-2">{top3[0].level?.level_name}</p>
                  <div className="flex items-center justify-center gap-2 bg-yellow-500/10 rounded-lg px-3 py-1.5">
                    <Dumbbell className="h-4 w-4 text-yellow-500" />
                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-yellow-500">{top3[0].workout_count}</span>
                  </div>
                  {top3[0].current_streak > 0 && (
                    <div className="flex items-center justify-center gap-1 mt-2">
                      <Flame className="h-3.5 w-3.5 text-orange-400" />
                      <span className="text-xs text-orange-400">{top3[0].current_streak} dage streak</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* 3rd Place */}
              <div className="order-3" data-testid="podium-3">
                <div className="bg-gradient-to-br from-amber-600/20 to-[#18181b] border border-amber-600/30 rounded-xl p-4 sm:p-6 text-center h-full flex flex-col justify-end">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 rounded-xl bg-amber-600/20 flex items-center justify-center">
                    <Medal className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
                  </div>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-2 rounded-full bg-zinc-700 flex items-center justify-center text-xl sm:text-2xl font-bold text-white border-2 border-amber-600/50">
                    {top3[2].avatar_initial}
                  </div>
                  <p className="font-bold text-white text-sm sm:text-base truncate">{top3[2].name}</p>
                  <p className="text-xs text-zinc-500 mb-2">{top3[2].level?.level_name}</p>
                  <div className="flex items-center justify-center gap-2">
                    <Dumbbell className="h-3.5 w-3.5 text-amber-500" />
                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-amber-500">{top3[2].workout_count}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rest of Leaderboard */}
          <div className="bg-[#18181b] border border-white/5 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#DFFF00]" />
                <span style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Top 10 Rangliste</span>
              </div>
              <span className="text-xs text-zinc-500">{period === "week" ? "Denne uge" : "Denne måned"}</span>
            </div>
            
            {leaderboard.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
                  <Trophy className="h-10 w-10 text-zinc-600" />
                </div>
                <p className="text-zinc-400 text-lg">Ingen data endnu</p>
                <p className="text-sm text-zinc-500 mt-1">Vær den første til at logge træninger!</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {leaderboard.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  const isTop3 = entry.rank <= 3;
                  
                  return (
                    <div
                      key={entry.user_id}
                      className={`flex items-center justify-between p-4 transition-colors ${
                        isCurrentUser ? 'bg-[#DFFF00]/5' : 'hover:bg-zinc-800/30'
                      }`}
                      data-testid={`leaderboard-rank-${entry.rank}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Rank */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                          entry.rank === 1 ? 'bg-yellow-500/20 text-yellow-500' :
                          entry.rank === 2 ? 'bg-zinc-400/20 text-zinc-400' :
                          entry.rank === 3 ? 'bg-amber-600/20 text-amber-600' :
                          'bg-zinc-800 text-zinc-400'
                        }`}>
                          {entry.rank === 1 ? <Crown className="h-5 w-5" /> :
                           entry.rank <= 3 ? <Medal className="h-5 w-5" /> :
                           <span style={{ fontFamily: 'JetBrains Mono' }} className="text-sm">{entry.rank}</span>}
                        </div>
                        
                        {/* Avatar & Name */}
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-base font-bold ${
                            isCurrentUser 
                              ? 'bg-[#DFFF00]/20 text-[#DFFF00]' 
                              : 'bg-zinc-800 text-white'
                          }`}>
                            {entry.avatar_initial}
                          </div>
                          <div>
                            <p className={`font-bold text-sm ${isCurrentUser ? 'text-[#DFFF00]' : 'text-white'}`}>
                              {entry.name}
                              {isCurrentUser && <span className="ml-2 text-xs opacity-70">(dig)</span>}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <span>{entry.level?.level_icon} {entry.level?.level_name}</span>
                              {entry.current_streak > 0 && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-zinc-600" />
                                  <span className="flex items-center gap-1 text-orange-400">
                                    <Flame className="h-3 w-3" />
                                    {entry.current_streak}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 sm:gap-8">
                        <div className="text-right hidden sm:block">
                          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">XP</p>
                          <p style={{ fontFamily: 'JetBrains Mono' }} className="text-sm font-bold text-white">
                            {entry.total_xp?.toLocaleString() || 0}
                          </p>
                        </div>
                        <div className="text-right min-w-[60px]">
                          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Træninger</p>
                          <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-[#DFFF00]">
                            {entry.workout_count}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Motivation for non-ranked users */}
          {myRank && !myRank.rank && (
            <div className="mt-8 bg-gradient-to-r from-[#18181b] via-[#1a1a1f] to-[#18181b] border border-white/5 rounded-xl p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
                <Dumbbell className="h-8 w-8 text-zinc-600" />
              </div>
              <p className="text-zinc-400 text-lg mb-2">Du er ikke på leaderboardet endnu</p>
              <p className="text-sm text-zinc-500 mb-4">Log din første træning for at komme med i konkurrencen!</p>
              <div className="inline-flex items-center gap-2 text-[#DFFF00]">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">Start din rejse i dag</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Paywall>
  );
}
